'use client';

import Image from 'next/image';
import Link from 'next/link';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';
import { useParams } from 'next/navigation';
import { isRTLLanguage } from '@/lib/i18n/config';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Feature {
  id: string;
  label: string;
  title: string;
  description: string;
  image: string;
}

// ─── Feature Band (matches Concept E exactly) ────────────────────────────────

interface FeatureBandProps {
  feature: Feature;
  index: number;
  imageRight: boolean;
  learnMoreLabel: string;
  locale: string;
}

function FeatureBand({ feature, index, imageRight, learnMoreLabel, locale }: FeatureBandProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-120px' });

  const num = String(index + 1).padStart(2, '0');

  return (
    <>
      {/* Gold separator */}
      <div className="h-px bg-gold/15 mx-6 md:mx-12" />

      <div
        ref={ref}
        className={cn(
          'flex flex-col md:flex-row min-h-[420px] md:min-h-[480px]',
          imageRight ? 'md:flex-row-reverse' : 'md:flex-row',
        )}
      >
        {/* Image — 60% */}
        <motion.div
          initial={{ opacity: 0, x: imageRight ? 32 : -32 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full md:w-[60%] min-h-[300px] md:min-h-0 overflow-hidden group"
        >
          <div className="absolute inset-4 md:inset-6 rounded-xl overflow-hidden">
            <Image
              src={feature.image}
              alt={feature.title}
              fill
              className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
              sizes="(max-width: 768px) 100vw, 60vw"
            />
            {/* Very subtle overlay to soften edges */}
            <div className="absolute inset-0 bg-navy/5 dark:bg-navy/10" />
          </div>
        </motion.div>

        {/* Text — 40% */}
        <motion.div
          initial={{ opacity: 0, x: imageRight ? -32 : 32 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full md:w-[40%] flex flex-col justify-center px-8 md:px-14 lg:px-16 py-12 md:py-16 bg-white dark:bg-navy-950"
        >
          {/* Large watermark number */}
          <span
            className="absolute top-8 right-8 md:top-10 md:right-10 font-display leading-none select-none pointer-events-none"
            style={{
              fontSize: 'clamp(4rem, 8vw, 7rem)',
              color: 'rgba(210,161,56,0.12)',
              lineHeight: 1,
            }}
            aria-hidden="true"
          >
            {num}
          </span>

          {/* Eyebrow: gold line + number */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-px bg-gold/50" />
            <span className="text-[10px] tracking-[0.4em] uppercase text-gold/70 font-medium">
              {num}
            </span>
          </div>

          {/* Title */}
          <h3 className="font-display text-2xl md:text-3xl font-semibold text-navy dark:text-white leading-snug mb-5">
            {feature.title}
          </h3>

          {/* Description */}
          <p className="text-navy/60 dark:text-white/60 leading-relaxed text-[0.9375rem] mb-8 max-w-sm">
            {feature.description}
          </p>

          {/* Learn More link — visible text includes the feature title so
              Google sees a descriptive link (fixes link-text SEO audit). */}
          <Link
            href={`/${locale}/treatments`}
            className="group/link inline-flex items-center gap-2 text-sm font-medium text-gold hover:text-gold-600 transition-colors duration-200 w-fit"
          >
            <span className="border-b border-gold/40 group-hover/link:border-gold-600 pb-px transition-colors duration-200">
              {learnMoreLabel} — {feature.title}
            </span>
            <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover/link:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function TrustBadges() {
  const { t } = useTranslation('home');
  const { t: tCommon } = useTranslation('common');
  const params = useParams();
  const rawLocale = params?.locale;
  const locale = typeof rawLocale === 'string' ? rawLocale : Array.isArray(rawLocale) ? rawLocale[0] ?? 'en' : 'en';
  const isRTL = isRTLLanguage(locale);

  const featuresBase: Omit<Feature, 'label'>[] = [
    {
      id: 'equipment',
      title: t('expertGuidance.cards.equipment.title'),
      description: t('expertGuidance.cards.equipment.description'),
      image: '/images/env/whitening.webp',
    },
    {
      id: 'expertise',
      title: t('expertGuidance.cards.expertise.title'),
      description: t('expertGuidance.cards.expertise.description'),
      image: '/images/env/personal.webp',
    },
    {
      id: 'care',
      title: t('expertGuidance.cards.care.title'),
      description: t('expertGuidance.cards.care.description'),
      image: '/images/env/global-care.webp',
    },
  ];

  const features: Feature[] = featuresBase.map((f, i) => ({
    ...f,
    label: String(i + 1).padStart(2, '0'),
  }));

  return (
    <section className="bg-white dark:bg-navy-950 py-4">
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div className="px-6 md:px-12 py-16 text-center">
          <span className="text-[10px] tracking-[0.35em] uppercase text-gold font-medium">
            {tCommon('expertGuidance.subtitle')}
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-navy dark:text-white mt-3 tracking-tight">
            {t('expertGuidance.title')}
          </h2>
          <div className="w-14 h-[1.5px] bg-gold mx-auto mt-4" />
        </div>

        {/* Full-width bands */}
        <div dir={isRTL ? 'rtl' : 'ltr'}>
          {features.map((feature, index) => {
            const imageRight = isRTL ? index % 2 === 0 : index % 2 === 1;

            return (
              <FeatureBand
                key={feature.id}
                feature={feature}
                index={index}
                imageRight={imageRight}
                learnMoreLabel={tCommon('expertGuidance.learnMore')}
                locale={locale}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}
